import { moneyToCents } from '../money-rounding';
import type { ShiftPayBreakdown } from '../schads';

/** The calculator's selected prices, retained beside its result. Never solved
 * from rounded money. Weak keys keep legacy persisted breakdown bytes stable. */
export interface EnginePricingEvidence {
  ordinaryAllocation: 'ACTUAL_DAY' | 'WHOLE_SHIFT';
  segments: Array<{
    start: string;
    end: string;
    day: 'weekday' | 'saturday' | 'sunday' | 'publicHoliday';
  }>;
  ordinaryRates: { weekday: number; saturday: number; sunday: number; publicHoliday: number };
  loadingRates: { evening: number; night: number };
  /** Actual engine operands: loading is rounded on the full span, then the
   * rounded OT backout is subtracted, retaining the existing compatibility floor. */
  loadingBackout?: {
    initialCents: number;
    correctedBackoutCents: number;
    compatibilityBackoutCents: number;
  };
  overtime: Array<{
    band: 'otFirst' | 'otRemainder' | 'otPublicHoliday';
    day: 'weekday' | 'saturday' | 'sunday' | 'publicHoliday';
    hours: number;
    rate: number;
  }>;
}
const evidence = new WeakMap<ShiftPayBreakdown, EnginePricingEvidence>();
export function rememberEnginePricingEvidence<T extends ShiftPayBreakdown>(
  result: T,
  selected: EnginePricingEvidence,
): T {
  const snapshot: EnginePricingEvidence = {
    ...selected,
    ordinaryRates: { ...selected.ordinaryRates },
    loadingRates: { ...selected.loadingRates },
    ...(selected.loadingBackout ? { loadingBackout: { ...selected.loadingBackout } } : {}),
    segments: selected.segments.map((segment) => ({ ...segment })),
    overtime: selected.overtime.map((part) => ({ ...part })),
  };
  for (const segment of snapshot.segments) Object.freeze(segment);
  for (const part of snapshot.overtime) Object.freeze(part);
  Object.freeze(snapshot.segments);
  Object.freeze(snapshot.overtime);
  Object.freeze(snapshot.ordinaryRates);
  Object.freeze(snapshot.loadingRates);
  if (snapshot.loadingBackout) Object.freeze(snapshot.loadingBackout);
  Object.freeze(snapshot);
  evidence.set(result, snapshot);
  return result;
}
export function enginePricingEvidence(
  result: ShiftPayBreakdown,
): EnginePricingEvidence | undefined {
  return evidence.get(result);
}

/** Normalize selected hourly prices, never money/quantity or a blended rate. */
export function enginePricingFactor(selectedRate: number, baseRate: number): number {
  // eslint-disable-next-line statura/no-floating-point-money -- Ratio of two engine-selected hourly prices; no amount/units recovery or money rounding.
  return selectedRate / baseRate;
}
/** Preserve the engine's native half-up band rounding for selected prices. */
export function enginePricingCents(hours: number, selectedRate: number): number {
  // eslint-disable-next-line statura/no-floating-point-money -- Mirror the existing engine band expression before canonical exact half-up conversion.
  return moneyToCents(hours * selectedRate);
}
